import type { Dataset } from "@/api/dataset";
import {
  readStorageBackendError,
  unsupportedStorageFeatures,
  type DatasetStorageBackend,
  type StorageFeature,
} from "@/api/datasetStorageBackend";

// 저장 위치의 사용자 문구. 판정은 `@/api/datasetStorageBackend` 가 하고 이 파일은 낱말과 문장만 만든다.
// 화면 낱말은 "저장 위치"다. 업로드 폼의 "업로드 서버 위치"(staging 위치)와 다른 축이다.

// 저장 위치의 화면 이름. 제품 이름이라 영어로 쓴다. 조회는 문자열로 한다. 서버가 이 화면보다 먼저 새
// 저장 위치를 낼 수 있다.
const STORAGE_BACKEND_LABELS: Readonly<Record<string, string>> = {
  gcp_storage: "GCS",
  azure_blob: "Azure",
} satisfies Record<DatasetStorageBackend, string>;

// 기능의 화면 이름. 공유 명사는 영어로 쓴다(docs/rules.md §11).
const STORAGE_FEATURE_LABELS: Record<StorageFeature, string> = {
  training: "Training",
  annotation: "Annotation",
  merge: "통합",
  diagnostics: "진단",
};

/** 저장 위치의 화면 이름. 이 화면이 모르는 값은 받은 그대로 적는다. 예) `"azure_blob"` → `"Azure"` */
export function storageBackendLabel(backend: string): string {
  return STORAGE_BACKEND_LABELS[backend] ?? backend;
}

/**
 * 저장 위치가 지원하지 않는 기능을 알리는 문장을 만든다.
 *
 * Args:
 *   backend: Dataset 의 저장 위치. 예) `"azure_blob"`
 *
 * Returns:
 *   모든 기능을 지원하면 null. 예) `"azure_blob"` →
 *   `"Azure에 저장된 Dataset입니다. 이 저장 위치는 Training·Annotation·통합 기능을 지원하지 않습니다."`
 */
export function storageBackendRestrictionText(backend: DatasetStorageBackend): string | null {
  const features = unsupportedStorageFeatures(backend);
  if (features.length === 0) {
    return null;
  }
  const names = features.map((feature) => STORAGE_FEATURE_LABELS[feature]).join("·");
  return `${storageBackendLabel(backend)}에 저장된 Dataset입니다. 이 저장 위치는 ${names} 기능을 지원하지 않습니다.`;
}

/**
 * 저장 위치가 그 기능을 지원하지 않는다는 문장을 만든다. 꺼진 버튼의 사유와 409 의 안내가 함께 쓴다.
 *
 * Args:
 *   backend: Dataset 의 저장 위치. 예) `"azure_blob"`
 *   feature: 지원하지 않는 기능. 예) `"training"`
 *
 * Returns:
 *   예) `"Azure에 저장된 Dataset은 Training 기능을 지원하지 않습니다."`
 */
export function storageFeatureUnsupportedText(
  backend: DatasetStorageBackend,
  feature: StorageFeature,
): string {
  return `${storageBackendLabel(backend)}에 저장된 Dataset은 ${STORAGE_FEATURE_LABELS[feature]} 기능을 지원하지 않습니다.`;
}

/**
 * Training 버튼을 끄는 사유. 저장 위치가 학습을 지원하지 않을 때만 값이 있다.
 *
 * 판정은 서버가 계산한 `training_supported` 다. 상태(available 여부)는 호출부가 따로 본다. 버튼을 숨기지
 * 않고 끈다. 저장 위치는 권한이 아니라 Dataset 의 성질이라서, 숨기면 왜 할 수 없는지 알 수 없다.
 *
 * Returns:
 *   학습할 수 있으면 null. 예) Azure Dataset → `"Azure에 저장된 Dataset은 Training 기능을 지원하지 않습니다."`
 */
export function trainingUnsupportedReason(
  dataset: Pick<Dataset, "storage_backend" | "training_supported">,
): string | null {
  return dataset.training_supported
    ? null
    : storageFeatureUnsupportedText(dataset.storage_backend, "training");
}

/**
 * 저장 위치 오류(409 미지원, 503 미설정)의 사용자 문구를 만든다.
 *
 * 서버 문장은 영어 개발자 문구다. 5xx 는 HTTP client 가 서버 문장을 버려서 원인이 보이지 않는다. 그래서
 * 오류 문구를 만드는 곳은 일반 409·5xx 분기보다 먼저 이 함수를 부른다.
 *
 * Returns:
 *   저장 위치 오류가 아니면 null.
 */
export function storageBackendErrorText(error: unknown): string | null {
  const detail = readStorageBackendError(error);
  if (detail === null) {
    return null;
  }
  if (detail.kind === "not_configured") {
    const location =
      detail.storageBackend === null
        ? "이 저장 위치"
        : `${storageBackendLabel(detail.storageBackend)} 저장 위치`;
    return `서버에 ${location}의 설정이 없어 요청을 처리하지 못했습니다. 운영자에게 문의해 주세요.`;
  }
  if (detail.storageBackend === null || detail.feature === null) {
    return "이 Dataset의 저장 위치는 요청한 기능을 지원하지 않습니다.";
  }
  return storageFeatureUnsupportedText(detail.storageBackend, detail.feature);
}
